import React, { useEffect, useState } from "react";
import {
  Building2,
  CheckCircle,
  RefreshCw,
  AlertCircle,
  ArrowLeft,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

function ProjectManager() {
  const [step, setStep] = useState(1);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    org_id: "",
    project_name: "",
    contact_person: "",
    address: "",
    phone_number: "",
    Website: "",
    onboarding_deduction: "",
    conversation_cost: "",
    notification_deduction: "",
    warm_lead_cost: "",
    hot_lead_cost: "",
  });

  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [responseMessage, setResponseMessage] = useState("");
  const [responseType, setResponseType] = useState(null);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const res = await fetch(`${BASE_URL}/orgdetails/getorganizations`);
        if (!res.ok) throw new Error("Failed to fetch organizations");
        const data = await res.json();
        setOrganizations(data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchOrganizations();
  }, [BASE_URL]);

  const handleChange = (event) => {
    const value =
      event.target.type === "number"
        ? parseFloat(event.target.value)
        : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: value,
    }));
  };

  const handleNext = () => {
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/orgdetails/addProject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error("Failed to add project");
      setShowSuccessModal(true);
      setFormData({
        org_id: "",
        project_name: "",
        contact_person: "",
        address: "",
        phone_number: "",
        Website: "",
        onboarding_deduction: "",
        conversation_cost: "",
        notification_deduction: "",
        warm_lead_cost: "",
        hot_lead_cost: "",
      });
      setResponseMessage("Project added successfully!");
      setResponseType("success");
    } catch (error) {
      console.error("Error submitting form:", error);
      setResponseMessage("Failed to add project.");
      setResponseType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const SuccessModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Success!</h3>
          <button
            onClick={() => setShowSuccessModal(false)}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="flex items-center justify-center mb-6">
          <div className="bg-green-100 rounded-full p-3">
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <p className="text-center text-gray-600 mb-6">
          Project has been created successfully!
        </p>
        <button
          onClick={() => {
            setShowSuccessModal(false);
            navigate("/projects");
          }}
          className="w-full bg-blue-600 text-white rounded-lg py-3 hover:bg-blue-700 transition-colors"
        >
          View All Projects
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Building2 className="h-6 w-6 text-blue-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                Project Manager
              </h1>
            </div>
            <button
              onClick={() => navigate("/projects")}
              className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Return
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Add New Project - Step {step} of 2
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {step === 1
                ? "Fill in the basic project details."
                : "Set up the project cost structure."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {step === 1 ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Project Name
                  </label>
                  <input
                    type="text"
                    name="project_name"
                    value={formData.project_name}
                    onChange={handleChange}
                    className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                    placeholder="Enter project name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Contact Person
                  </label>
                  <input
                    type="text"
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleChange}
                    className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                    placeholder="Enter contact person name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Project Location
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                    placeholder="Enter project location"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Project Website Link
                  </label>
                  <input
                    type="url"
                    name="Website"
                    value={formData.Website}
                    onChange={handleChange}
                    className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                    placeholder="Enter project Website Link"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Contact Number
                  </label>
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                    placeholder="Enter contact number"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Organization
                  </label>
                  <select
                    name="org_id"
                    value={formData.org_id}
                    onChange={handleChange}
                    required
                    className="block w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:outline-none focus:ring-blue-500 transition-colors sm:text-sm"
                  >
                    <option value="">Select your organization</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.org_name}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  type="button"
                  onClick={handleNext}
                  className="w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-lg text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Next Step
                </button>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Onboarding Deduction
                  </label>
                  <input
                    type="number"
                    name="onboarding_deduction"
                    value={formData.onboarding_deduction}
                    onChange={handleChange}
                    className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                    placeholder="Enter onboarding deduction"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Conversation Cost
                  </label>
                  <input
                    type="number"
                    name="conversation_cost"
                    value={formData.conversation_cost}
                    onChange={handleChange}
                    className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                    placeholder="Enter conversation cost"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Notification Deduction
                  </label>
                  <input
                    type="number"
                    name="notification_deduction"
                    value={formData.notification_deduction}
                    onChange={handleChange}
                    className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                    placeholder="Enter notification deduction"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Warm Lead Cost
                  </label>
                  <input
                    type="number"
                    name="warm_lead_cost"
                    value={formData.warm_lead_cost}
                    onChange={handleChange}
                    className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                    placeholder="Enter warm lead cost"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Hot Lead Cost
                  </label>
                  <input
                    type="number"
                    name="hot_lead_cost"
                    value={formData.hot_lead_cost}
                    onChange={handleChange}
                    className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                    placeholder="Enter hot lead cost"
                    required
                  />
                </div>

                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="w-1/2 flex justify-center items-center px-6 py-3 border border-gray-300 rounded-lg text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-1/2 flex justify-center items-center px-6 py-3 border border-transparent rounded-lg text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSubmitting ? (
                      <RefreshCw className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Save Project
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </form>

          {responseMessage && (
            <div
              className={`px-6 py-4 border-t border-gray-200 ${
                responseType === "success" ? "bg-green-50" : "bg-red-50"
              }`}
            >
              <div className="flex items-center space-x-3">
                {responseType === "success" ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-500" />
                )}
                <p
                  className={`text-sm font-medium ${
                    responseType === "success"
                      ? "text-green-800"
                      : "text-red-800"
                  }`}
                >
                  {responseMessage}
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
      {showSuccessModal && <SuccessModal />}
    </div>
  );
}

export default ProjectManager;
